import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const PART_COLORS = ['bg-sky-100 text-sky-900', 'bg-amber-100 text-amber-900', 'bg-violet-100 text-violet-900', 'bg-emerald-100 text-emerald-900'];

function rangesOverlap(a, b) {
  return a.start < b.end && b.start < a.end;
}

function buildPattern(sample, ranges) {
  const ordered = [...ranges].sort((a, b) => a.start - b.start);
  if (ordered.length < 2 || !sample) return null;
  const delimiters = [];
  for (let i = 0; i < ordered.length - 1; i += 1) {
    const gap = sample.slice(ordered[i].end, ordered[i + 1].start);
    if (!gap) return null;
    delimiters.push(gap);
  }
  return {
    sample,
    delimiters,
    parts: ordered.map((range) => ({
      name: range.name,
      sample_value: sample.slice(range.start, range.end),
      procore_segment_id: range.procore_segment_id || null,
      procore_segment_type: range.procore_segment_type || null,
      procore_segment_name: range.procore_segment_name || null,
    })),
  };
}

function rangesFromPattern(pattern) {
  if (!pattern?.parts?.length || !pattern.sample) return [];
  const ranges = [];
  let cursor = 0;
  pattern.parts.forEach((part, index) => {
    const value = part.sample_value || '';
    const start = pattern.sample.indexOf(value, cursor);
    if (start < 0) return;
    const end = start + value.length;
    ranges.push({
      id: `${start}-${end}-${index}`,
      start,
      end,
      name: part.name,
      procore_segment_id: part.procore_segment_id || '',
      procore_segment_type: part.procore_segment_type || '',
      procore_segment_name: part.procore_segment_name || '',
    });
    cursor = end;
    const delimiter = (pattern.delimiters || [])[index];
    if (delimiter) cursor += delimiter.length;
  });
  return ranges;
}

function indexFromPoint(clientX, clientY) {
  const node = document.elementFromPoint(clientX, clientY);
  const el = node?.closest?.('[data-cc-index]');
  if (!el) return null;
  const index = Number(el.getAttribute('data-cc-index'));
  return Number.isInteger(index) ? index : null;
}

export default function ProjectCostCodeSegments({ projectVuid, enabled, pattern, onSaved, className }) {
  const [sample, setSample] = useState(pattern?.sample || '');
  const [ranges, setRanges] = useState(() => rangesFromPattern(pattern));
  const [pendingName, setPendingName] = useState('');
  const [selection, setSelection] = useState(null);
  const [projectCodes, setProjectCodes] = useState([]);
  const [segments, setSegments] = useState([]);
  const [segmentError, setSegmentError] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setSample(pattern?.sample || '');
    setRanges(rangesFromPattern(pattern));
  }, [pattern]);

  useEffect(() => {
    if (!projectVuid || !enabled) return undefined;
    let cancelled = false;
    axios.get(`${baseURL}/api/projects/${projectVuid}/cost-codes`)
      .then((response) => {
        if (cancelled) return;
        const rows = (response.data || []).filter((row) => row.is_project_specific && row.code);
        setProjectCodes(rows);
        setSample((current) => current || pattern?.sample || rows[0]?.code || '');
      })
      .catch(() => {
        if (!cancelled) setProjectCodes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid, enabled, pattern]);

  const mappableSegments = useMemo(
    () => (segments || []).filter((row) => row.type !== 'line_item_type'),
    [segments]
  );

  const applyDrag = (start, hover, text = sample) => {
    if (start == null || hover == null) return;
    const from = Math.min(start, hover);
    const to = Math.max(start, hover) + 1;
    setSelection({ start: from, end: to, text: text.slice(from, to) });
  };

  const beginHighlight = (event) => {
    const el = event.target.closest?.('[data-cc-index]');
    if (!el) return;
    event.preventDefault();
    const start = Number(el.getAttribute('data-cc-index'));
    if (!Number.isInteger(start)) return;
    const text = sample;
    applyDrag(start, start, text);
    const move = (moveEvent) => applyDrag(start, indexFromPoint(moveEvent.clientX, moveEvent.clientY), text);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const nameSelection = () => {
    if (!selection || !pendingName.trim()) return;
    const next = { ...selection, name: pendingName.trim(), id: `${selection.start}-${selection.end}` };
    if (ranges.some((range) => rangesOverlap(range, next))) {
      setMessage('That selection overlaps an existing named segment.');
      return;
    }
    setRanges((current) => [...current, next].sort((a, b) => a.start - b.start));
    setPendingName('');
    setSelection(null);
    setMessage('');
  };

  const splitOnDelimiter = () => {
    const delimiter = ['.', '/'].find((mark) => sample.includes(mark) && sample.split(mark).length >= 2);
    if (!delimiter) {
      setMessage('This cost code has no . or / to split on. Drag across the characters instead.');
      return;
    }
    const parts = [];
    let cursor = 0;
    sample.split(delimiter).forEach((piece) => {
      if (piece) {
        parts.push({
          id: `${cursor}-${cursor + piece.length}`,
          start: cursor,
          end: cursor + piece.length,
          name: '',
          procore_segment_id: '',
        });
      }
      cursor += piece.length + delimiter.length;
    });
    if (parts.length < 2) {
      setMessage('Need at least two pieces to name.');
      return;
    }
    setRanges(parts.map((part, index) => ({ ...part, name: `Segment ${index + 1}` })));
    setSelection(null);
    setMessage('Named the pieces in order. Rename them and map each one to Procore.');
  };

  const loadProcoreSegments = async () => {
    setSegmentError('');
    try {
      const response = await axios.get(`${baseURL}/api/projects/${projectVuid}/procore-wbs-segments`);
      setSegments(response.data.segments || []);
    } catch (error) {
      setSegmentError(error.response?.data?.error || error.message || 'Failed to load Procore segments');
      setSegments([]);
    }
  };

  const assignSegment = (rangeId, segmentId) => {
    const segment = mappableSegments.find((row) => String(row.id) === String(segmentId));
    setRanges((current) => current.map((range) => (
      range.id === rangeId
        ? {
            ...range,
            procore_segment_id: segment ? segment.id : '',
            procore_segment_type: segment ? segment.type : '',
            procore_segment_name: segment ? segment.name : '',
          }
        : range
    )));
  };

  const savePattern = async () => {
    const named = ranges.filter((range) => range.name && range.name.trim());
    const nextPattern = buildPattern(sample, named);
    if (!nextPattern) {
      setMessage('Highlight and name at least two pieces of the cost code, with the delimiter left unhighlighted.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      await axios.put(`${baseURL}/api/projects/${projectVuid}/settings`, {
        enable_custom_cost_code_segments: true,
        cost_code_segment_pattern: nextPattern,
      });
      setMessage('Saved. Cost codes stay whole in Claret; Procore will use these named parts.');
      onSaved?.();
    } catch (error) {
      setMessage(error.response?.data?.error || error.message || 'Failed to save segment pattern');
    } finally {
      setSaving(false);
    }
  };

  if (!enabled) return null;

  return (
    <div className={`bg-white rounded-xl shadow-lg p-8 mb-8${className ? ` ${className}` : ''}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Cost Code Segments</h2>
          <p className="text-sm text-gray-500 mt-1">
            Drag across characters in the cost code to highlight a piece, then name it. Leave the delimiter (like a period) unhighlighted. Cost type stays separate.
          </p>
        </div>
        <button type="button" className="btn-claret" onClick={loadProcoreSegments}>
          Load Procore Segments
        </button>
      </div>

      {segmentError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-600 font-medium">{segmentError}</p>
        </div>
      ) : null}

      {segments.length > 0 ? (
        <div className="mb-6 border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-medium text-gray-900 mb-2">Procore WBS on this project</div>
          <ul className="space-y-1 text-sm text-gray-700">
            {segments.map((segment) => (
              <li key={segment.id}>
                {segment.name}
                {segment.required ? ' (required)' : ' (optional)'}
                {segment.type === 'line_item_type'
                  ? ' — stays a separate Claret field, not part of the cost code'
                  : ' — map a named piece of the cost code to this'}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {projectCodes.length > 0 ? (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="segment-code-pick">
            Project cost code to highlight
          </label>
          <select
            id="segment-code-pick"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-transparent font-mono"
            value={projectCodes.some((row) => row.code === sample) ? sample : ''}
            onChange={(event) => {
              setSample(event.target.value);
              setRanges([]);
              setSelection(null);
            }}
          >
            <option value="">Choose a project cost code…</option>
            {projectCodes.map((row) => (
              <option key={row.vuid} value={row.code}>
                {row.code}{row.description ? ` — ${row.description}` : ''}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="segment-sample">
            Sample project cost code
          </label>
          <input
            id="segment-sample"
            value={sample}
            onChange={(event) => {
              setSample(event.target.value);
              setRanges([]);
              setSelection(null);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-transparent font-mono"
            placeholder="01.04-200"
          />
        </div>
      )}

      {sample ? (
        <div className="mt-2">
          <div className="text-sm font-medium text-gray-700 mb-2">Highlight a piece</div>
          <div
            className="inline-flex flex-wrap items-center gap-0.5 rounded-lg border border-gray-300 bg-gray-50 px-3 py-3 select-none cursor-text"
            onPointerDown={beginHighlight}
          >
            {sample.split('').map((character, index) => {
              const ordered = [...ranges].sort((a, b) => a.start - b.start);
              const namedIndex = ordered.findIndex((range) => index >= range.start && index < range.end);
              const named = namedIndex >= 0 ? ordered[namedIndex] : null;
              const highlighted = selection && index >= selection.start && index < selection.end;
              let color = 'bg-white text-gray-900';
              if (named) color = PART_COLORS[namedIndex % PART_COLORS.length];
              if (highlighted) color = 'bg-blue-600 text-white';
              return (
                <button
                  type="button"
                  key={`${character}-${index}`}
                  data-cc-index={index}
                  draggable={false}
                  title={named?.name || 'Drag to highlight'}
                  aria-label={`Cost code character ${index + 1}: ${character === ' ' ? 'space' : character}`}
                  className={`min-w-[1.1rem] p-0 border-0 text-center font-mono text-xl leading-8 rounded ${color}`}
                >
                  {character === ' ' ? '\u00a0' : character}
                </button>
              );
            })}
          </div>
          <div className="mt-3">
            <button type="button" className="px-4 py-2 border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50 font-medium" onClick={splitOnDelimiter}>
              Split on delimiter
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Choose or enter a project cost code to highlight.</p>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="segment-name">
            Name for highlighted text {selection ? `"${selection.text}"` : ''}
          </label>
          <input
            id="segment-name"
            value={pendingName}
            onChange={(event) => setPendingName(event.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-transparent"
            placeholder="Sub Job"
          />
        </div>
        <button type="button" className="btn-claret" onClick={nameSelection} disabled={!selection || !pendingName.trim()}>
          Name Segment
        </button>
      </div>

      {ranges.length > 0 ? (
        <div className="mt-6 space-y-3">
          {ranges.map((range) => (
            <div key={range.id} className="flex flex-wrap items-center gap-3 border border-gray-200 rounded-lg p-3">
              <div className="min-w-[8rem]">
                <input
                  value={range.name}
                  onChange={(event) => setRanges((current) => current.map((item) => (
                    item.id === range.id ? { ...item, name: event.target.value } : item
                  )))}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm font-semibold"
                />
                <div className="font-mono text-sm text-gray-600 mt-1">{sample.slice(range.start, range.end)}</div>
              </div>
              <select
                className="flex-1 min-w-[12rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-transparent"
                value={range.procore_segment_id || ''}
                onChange={(event) => assignSegment(range.id, event.target.value)}
              >
                <option value="">Map to Procore segment…</option>
                {mappableSegments.map((segment) => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name} ({segment.type || 'custom'})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50 font-medium"
                onClick={() => setRanges((current) => current.filter((item) => item.id !== range.id))}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {mappableSegments.length === 0 ? (
        <p className="text-sm text-gray-500 mt-4">
          Load Procore segments to see Sub Job and other WBS pieces, then map each named cost-code piece. Cost type stays its own Claret field.
        </p>
      ) : null}

      {message ? <p className="text-sm text-gray-700 mt-4">{message}</p> : null}

      <div className="mt-6">
        <button type="button" className="btn-claret" onClick={savePattern} disabled={saving}>
          {saving ? 'Saving…' : 'Save Segment Pattern'}
        </button>
      </div>
    </div>
  );
}
